import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  Alert,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  LinearProgress,
  Chip,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  setApplicationStep,
  setUkAssessmentQuestions,
  setUkAssessmentAnswer,
  setUkAssessmentResult,
  useOnboarding,
  UkAssessmentQuestion,
  UkAssessmentAnswer,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";

export default function UKAppropriatenessAssessment() {
  const dispatch = useDispatch();
  const { ukOnboarding } = useOnboarding();
  const { assessment } = ukOnboarding;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState<string | null>(null);

  const [getQuestions, { isLoading: isLoadingQuestions }] = api.useLazyGetAssessmentQuestionsQuery();
  const [submitAssessment, { isLoading: isSubmitting }] = api.useSubmitAssessmentMutation();
  const [getAssessmentStatus] = api.useLazyGetAssessmentStatusQuery();

  // Check for lockout status on mount
  useEffect(() => {
    checkAssessmentStatus();
  }, []);

  const checkAssessmentStatus = async () => {
    try {
      const status = await getAssessmentStatus().unwrap();

      // Already past the assessment gate — a graded pass or a result in the
      // manual-review band (score 80-94). Send the user to the result screen
      // rather than serving a fresh set of questions.
      if (status.passed) {
        dispatch(
          setUkAssessmentResult({
            status: "passed",
            manualReview: !!status.manualReview,
            attemptNumber: status.attemptCount,
          }),
        );
        dispatch(setApplicationStep("ukAssessmentResult"));
        return;
      }

      // 24h wait after a second fail: show the cooling-off style countdown
      // page. Checked before the lockout branch because the status endpoint
      // reports the wait as a lockout (lockoutEndsAt = wait end) too.
      if (status.nextAttemptAvailableAt && status.nextAttemptAvailableAt > Date.now()) {
        dispatch(
          setUkAssessmentResult({
            status: "failed",
            attemptNumber: status.attemptCount,
            nextAttemptAvailableAt: status.nextAttemptAvailableAt,
          }),
        );
        dispatch(setApplicationStep("ukAssessmentResult"));
        return;
      }

      if (status.isLockedOut && status.lockoutEndsAt) {
        setIsLocked(true);
        const days = Math.max(
          1,
          Math.ceil((status.lockoutEndsAt - Date.now()) / (24 * 60 * 60 * 1000)),
        );
        const lockoutDate = new Date(status.lockoutEndsAt).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        setLockoutMessage(
          `You have exceeded the maximum number of attempts. You can re-apply after ${days} day${
            days === 1 ? "" : "s"
          } (${lockoutDate}).`,
        );
        return;
      }

      if (status.passed) {
        // Already passed on the backend — show the "you have passed" screen
        // and let the user acknowledge it to continue where they left off.
        dispatch(setUkAssessmentResult({ status: "passed" }));
        dispatch(setApplicationStep("ukAssessmentResult"));
        return;
      }

      // Load questions if not locked
      if (assessment.questions.length === 0) {
        loadQuestions();
      }
    } catch (err) {
      console.error("[UK Assessment] Failed to check assessment status:", err);
      // If we can't check status, try to load questions
      console.log('[UK Assessment] Falling back to loading questions directly...');
      if (assessment.questions.length === 0) {
        loadQuestions();
      }
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await getQuestions().unwrap();

      // The endpoint declines with a 200 + success:false (locked out, attempts
      // exhausted, already passed) — surface it instead of spinning forever on
      // an empty question list.
      if (!response.success || response.questions.length === 0) {
        if (response.isLockedOut) {
          setIsLocked(true);
          let until = "";
          if (response.lockoutEndsAt) {
            const days = Math.max(
              1,
              Math.ceil((response.lockoutEndsAt - Date.now()) / (24 * 60 * 60 * 1000)),
            );
            const date = new Date(response.lockoutEndsAt).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
            });
            until = ` You can re-apply after ${days} day${days === 1 ? "" : "s"} (${date}).`;
          }
          setLockoutMessage(
            (response.message || "You are currently locked out of the assessment.") + until,
          );
        } else {
          setError(response.message || "Failed to load assessment questions. Please try again.");
        }
        return;
      }

      const mappedQuestions: UkAssessmentQuestion[] = response.questions.map((q: any) => ({
        id: q.id,
        type: q.type.toLowerCase() as UkAssessmentQuestion["type"],
        questionText: q.questionText,
        options: q.options,
      }));
      dispatch(setUkAssessmentQuestions(mappedQuestions));
    } catch (err: any) {
      if (err?.data?.isLockedOut) {
        setIsLocked(true);
        setLockoutMessage(err?.data?.message || "You are currently locked out of the assessment.");
      } else {
        setError("Failed to load assessment questions. Please try again.");
      }
    }
  };

  const currentQuestion = assessment.questions[currentQuestionIndex];
  const totalQuestions = assessment.questions.length;
  const progress = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;

  // Check if current question is already answered
  useEffect(() => {
    if (currentQuestion) {
      const existingAnswer = assessment.answers.find(
        (a) => a.questionId === currentQuestion.id,
      );
      setSelectedAnswer(existingAnswer?.selectedOptionId || null);
    }
  }, [currentQuestionIndex, currentQuestion, assessment.answers]);

  const handleAnswerSelect = (optionId: string) => {
    setSelectedAnswer(optionId);
    setError(null);
  };

  const handleNext = () => {
    if (!selectedAnswer) {
      setError("Please select an answer before continuing.");
      return;
    }

    // Save the answer
    const answer: UkAssessmentAnswer = {
      questionId: currentQuestion.id,
      selectedOptionId: selectedAnswer,
    };
    dispatch(setUkAssessmentAnswer(answer));

    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAnswer) {
      setError("Please select an answer before submitting.");
      return;
    }

    // Save the final answer
    const finalAnswer: UkAssessmentAnswer = {
      questionId: currentQuestion.id,
      selectedOptionId: selectedAnswer,
    };
    dispatch(setUkAssessmentAnswer(finalAnswer));

    // Prepare all answers for submission
    const allAnswers = [
      ...assessment.answers.filter((a) => a.questionId !== currentQuestion.id),
      finalAnswer,
    ];

    try {
      const result = await submitAssessment({
        submitAssessmentDto: {
          answers: allAnswers.map((a) => ({
            questionId: a.questionId,
            selectedOptionId: a.selectedOptionId,
          })),
        },
      }).unwrap();

      // Update the assessment result in state
      dispatch(
        setUkAssessmentResult({
          status: result.passed || result.manualReviewRequired ? "passed" : "failed",
          score: result.score,
          totalQuestions: result.totalQuestions,
          knockoutFailed: result.knockoutFailed,
          passedWithWarning: result.passedWithWarning,
          manualReview: result.manualReviewRequired,
          attemptNumber: result.attemptNumber,
          lockoutEndsAt: result.lockoutEndsAt,
          nextAttemptAvailableAt: result.nextAttemptAvailableAt,
        }),
      );

      // Navigate to result page
      dispatch(setApplicationStep("ukAssessmentResult"));
    } catch (err: any) {
      setError(err?.data?.message || "Failed to submit assessment. Please try again.");
    }
  };

  const handleBack = () => {
    dispatch(setApplicationStep("ukInvestorType"));
  };

  const handleRetry = () => {
    setError(null);
    checkAssessmentStatus();
  };

  if (isLocked) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
            <Typography variant="h5" fontWeight={600} gutterBottom color="error">
              Assessment Temporarily Unavailable
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {lockoutMessage}
            </Typography>
            <Button variant="outlined" color="inherit" size="large" onClick={handleBack}>
              Back to Investor Type
            </Button>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  // Question load failed (and we have nothing to show) — surface the error
  // with a way out instead of trapping the user on the loading spinner.
  if (error && !isLoadingQuestions && assessment.questions.length === 0) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
            <Typography variant="h6" gutterBottom color="error">
              Unable to load the assessment
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {error}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              mt={2}
            >
              <Button variant="outlined" color="inherit" size="large" onClick={handleBack}>
                Back
              </Button>
              <Button variant="contained" color="primary" size="large" onClick={handleRetry}>
                Try Again
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  if (isLoadingQuestions || assessment.questions.length === 0) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              Loading Assessment Questions...
            </Typography>
            <LinearProgress sx={{ mt: 2 }} />
          </Paper>
        </Grid>
      </Grid>
    );
  }

  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={600}>
            Appropriateness Assessment
          </Typography>
          <Chip
            label={`Attempt ${assessment.attemptNumber + 1} of ${assessment.maxAttempts}`}
            color={assessment.attemptNumber >= 2 ? "warning" : "default"}
            size="small"
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Please answer the following questions to help us assess your understanding of
          cryptoassets.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}% Complete
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Chip
            label={
              currentQuestion.type === "experience"
                ? "Trading Experience"
                : currentQuestion.type === "professional"
                  ? "Professional Knowledge"
                  : "Knowledge"
            }
            size="small"
            color={
              currentQuestion.type === "experience"
                ? "primary"
                : currentQuestion.type === "professional"
                  ? "info"
                  : "secondary"
            }
            sx={{ mb: 2 }}
          />
          <Typography variant="h6" fontWeight={500} gutterBottom>
            {currentQuestion.questionText}
          </Typography>

          <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
            <RadioGroup
              value={selectedAnswer || ""}
              onChange={(e) => handleAnswerSelect(e.target.value)}
            >
              {currentQuestion.options.map((option) => (
                <Paper
                  key={option.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1,
                    border:
                      selectedAnswer === option.id
                        ? "2px solid #1976d2"
                        : "1px solid #e0e0e0",
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "#1976d2",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  onClick={() => handleAnswerSelect(option.id)}
                >
                  <FormControlLabel
                    value={option.id}
                    control={<Radio />}
                    label={option.text}
                    sx={{ m: 0, width: "100%" }}
                  />
                </Paper>
              ))}
            </RadioGroup>
          </FormControl>
        </Paper>
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}

      <Grid item xs={12} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          mt={2}
        >
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </Button>

          {isLastQuestion ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={!selectedAnswer || isSubmitting}
              sx={{ minWidth: 200 }}
            >
              {isSubmitting ? "Submitting..." : "Submit Assessment"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleNext}
              disabled={!selectedAnswer}
              sx={{ minWidth: 200 }}
            >
              Next Question
            </Button>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
}
